import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Lock } from "lucide-react";
import { api, formatApiError, money, imgSrc } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

export default function Checkout() {
  const { items, subtotal } = useCart();
  const { user } = useAuth();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [promo, setPromo] = useState("");
  const [promoInfo, setPromoInfo] = useState(null);
  const [promoErr, setPromoErr] = useState("");
  const [checking, setChecking] = useState(false);
  const [zone, setZone] = useState("uk");

  const applyPromo = async () => {
    setPromoErr("");
    setChecking(true);
    try {
      const { data } = await api.post("/promos/validate", { code: promo });
      if (data.valid) {
        setPromoInfo(data);
      } else {
        setPromoInfo(null);
        setPromoErr("That code is not recognised");
      }
    } catch (e) {
      setPromoErr(formatApiError(e));
    } finally {
      setChecking(false);
    }
  };

  const pay = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        items: items.map(({ product_id, size, quantity }) => ({ product_id, size, quantity })),
        email: user ? undefined : email,
        origin_url: window.location.origin,
        promo_code: promoInfo?.valid ? promo : undefined,
        delivery_zone: zone,
      });
      if (data?.checkout_url) {
        const win = window.open(data.checkout_url, "_blank");
        if (win) {
          setLoading(false);
          toast.info("Finish your payment in the new tab — your bag stays saved here.");
        } else {
          window.location.href = data.checkout_url;
        }
      }
    } catch (err) {
      setError(formatApiError(err));
      setLoading(false);
    }
  };

  if (!items.length) {
    return (
      <main data-testid="checkout-empty" className="mx-auto max-w-3xl px-5 pb-32 pt-40">
        <h1 className="font-display text-6xl font-light">Your bag is <em>empty.</em></h1>
        <Link to="/shop" className="mt-8 inline-flex rounded-full bg-ink px-7 py-4 text-sm text-bone" data-testid="checkout-shop-link">Browse garments</Link>
      </main>
    );
  }

  return (
    <main data-testid="checkout-page" className="mx-auto grid max-w-[1400px] gap-14 px-5 pb-32 pt-28 md:grid-cols-12 md:px-10 md:pt-36">
      <form onSubmit={pay} className="md:col-span-6">
        <p className="eyebrow">Step 01 — Details</p>
        <h1 className="mt-4 font-display text-6xl font-light leading-none md:text-7xl">Checkout</h1>
        {user ? (
          <p data-testid="checkout-signed-in" className="mt-10 border border-line bg-sand p-5 text-sm">Signed in as <strong>{user.email}</strong>. Your order will be saved to your account.</p>
        ) : (
          <div className="mt-10">
            <label className="eyebrow" htmlFor="email">Email for your receipt</label>
            <input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="checkout-email-input" placeholder="you@email.com" className="mt-3 w-full border-b border-ink bg-transparent py-3 text-lg outline-none placeholder:text-stone/70" />
            <p className="mt-4 text-sm text-stone">Checking out as a guest. <Link to="/login" className="link-underline text-ink" data-testid="checkout-login-link">Sign in</Link> to keep your order history.</p>
          </div>
        )}
        <p className="mt-10 text-sm text-stone">Shipping address and card details are collected on the next step by Stripe.</p>
        {error && <p data-testid="checkout-error" className="mt-6 text-sm text-signal">{error}</p>}
        <div className="mt-6" data-testid="delivery-zone-section">
          <label className="eyebrow" htmlFor="zone">Deliver to</label>
          <select id="zone" value={zone} onChange={(e) => setZone(e.target.value)} data-testid="delivery-zone-select" className="mt-2 w-full border border-line bg-white px-3 py-3 text-sm outline-none focus:border-ink">
            <option value="uk">United Kingdom</option>
            <option value="eu">Europe</option>
          </select>
          <p className="mt-2 text-xs text-stone" data-testid="delivery-rates-note">
            UK: £3.95 standard, free over £50 · Europe: £4.95 standard, free over £100 · Express £9.95 (1-2 days)
          </p>
        </div>
        <button type="submit" disabled={loading} data-testid="checkout-pay-button" className="mt-8 flex w-full items-center justify-center gap-3 rounded-full bg-ink py-5 text-sm text-bone transition-colors duration-300 hover:bg-signal disabled:opacity-60">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
          Continue to payment — {money(subtotal)}
        </button>
      </form>
      <aside className="md:col-span-5 md:col-start-8" data-testid="checkout-summary">
        <p className="eyebrow">Order summary</p>
        <ul className="mt-6 divide-y divide-line border-y border-line">
          {items.map((it) => (
            <li key={`${it.product_id}-${it.size}`} className="flex gap-4 py-4">
              <img src={imgSrc(it.image)} alt={it.name} className="h-24 w-[72px] object-cover" />
              <div className="flex flex-1 justify-between">
                <div>
                  <p className="font-display text-xl">{it.name}</p>
                  <p className="eyebrow mt-1">{it.size} × {it.quantity}</p>
                </div>
                <span className="font-mono text-sm">{money(it.price * it.quantity)}</span>
              </div>
            </li>
          ))}
        </ul>
        <div className="mt-6 flex justify-between font-display text-2xl">
          <span>Subtotal</span><span data-testid="checkout-subtotal" className="font-mono text-lg">{money(subtotal)}</span>
        </div>
        <div className="mt-6 border-t border-line pt-5" data-testid="promo-section">
          <p className="eyebrow">Promo code</p>
          <div className="mt-3 flex gap-2">
            <input
              value={promo}
              onChange={(e) => setPromo(e.target.value.toUpperCase())}
              disabled={!!promoInfo?.valid}
              placeholder="LAUNCH10"
              data-testid="promo-input"
              className="flex-1 border border-line bg-white px-3 py-2 font-mono text-sm outline-none focus:border-ink disabled:opacity-60"
            />
            {promoInfo?.valid ? (
              <span data-testid="promo-applied" className="flex items-center gap-2 px-3 text-xs text-green-700">✓ Applied</span>
            ) : (
              <button type="button" onClick={applyPromo} disabled={!promo || checking} data-testid="promo-apply-button" className="rounded-full border border-ink px-5 text-sm transition-colors hover:bg-ink hover:text-bone disabled:opacity-50">
                {checking ? "…" : "Apply"}
              </button>
            )}
          </div>
          {promoInfo?.valid && (
            <p data-testid="promo-note" className="mt-2 text-xs text-stone">
              {promoInfo.code} — {promoInfo.percent_off ? `${promoInfo.percent_off}% off` : `£${promoInfo.amount_off.toFixed(2)} off`} applied at the payment step.
            </p>
          )}
          {promoErr && <p data-testid="promo-error" className="mt-2 text-xs text-signal">{promoErr}</p>}
        </div>
      </aside>
    </main>
  );
}
